import { Injectable } from "@angular/core";
import { MaintenanceHubConnectionService } from "@app/core/services/hubs/maintenance-hub-connection.service";
import { Maintenance } from "@app/core/services/hubs/models/maintenance.model";
import { AlertValidationResult, TagAlert } from "@app/core/services/hubs/models/tag-alert.model";
import { PostOfficeService } from "@app/core/services/post-office/post-office.service";
import { SettingsService } from "@app/core/services/settings/settings.service";
import { AlertType, SecurityAlert } from "@app/shared/toasts/security-alert/models/security-alert";
import { SecurityAlertComponent } from "@app/shared/toasts/security-alert/security-alert.component";
import { TranslateService } from "@ngx-translate/core";
import { cloneDeep } from "lodash";
import { GlobalConfig, ToastrService } from "ngx-toastr";
import { BehaviorSubject, Observable } from "rxjs";
import { Router } from "@angular/router";
// Alert Options Interface
export interface GeneralAlertOptions {
    title: string;
    content: string;
    alertType: AlertType;
    photoUrl?: string;
    showImage?: boolean;
    detailsUrl?: string;
    detailsUrlParams?: any;
    locateUrl?: string;
    locateUrlParams?: any;
    disableTimeOut?: boolean;
    timeOut?: number;
    tapToDismiss?: boolean;
    closeButton?: boolean;
    preventDuplicates?: boolean;
}

// Toast Timeout Configuration - Tüm süreleri buradan kolayca ayarlayabilirsin
export const TOAST_TIMEOUTS = {
    // Hızlı toast metodları için
    SUCCESS: 8000,          // 8 saniye
    ERROR: 30000,           // 30 saniye
    INFO: 12000,            // 12 saniye
    WARNING: 20000,         // 20 saniye

    // Genel default'lar
    DEFAULT_NORMAL: 15000,  // 15 saniye (normal alerts için)
    DEFAULT_ERROR: 30000,   // 30 saniye (error alerts için)

    // Özel durumlar
    SECURITY_ALERT: 20000,  // 20 saniye (güvenlik alertleri)
    MAINTENANCE: 15000,     // 15 saniye (maintenance completed)

    // Test/Debug
    TEST_LONG: 300000,      // 5 dakika (test toastları için)
    TEST_SHORT: 5000,       // 5 saniye (hızlı test için)
} as const;

@Injectable({
    providedIn: 'root'
})
export class ToastNotifyService {    // Bildirim kuyruğu ve sessize alma ayarları
    private alertQueue: TagAlert[] = [];
    private generalAlertQueue: GeneralAlertOptions[] = []; // Genel alertler için kuyruk
    private muteDuration = 60; // Sessize alma süresi saniye olarak (örnek: 60 saniye)
    private countdown$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    private countdownInterval: any;
    private _isMuted: boolean;
    private activeToastMap: Map<string, any> = new Map(); // Toast hash -> toast reference mapping

    // Mute sırasında gizlenen toast'ları saklamak için
    private hiddenToastsBeforeMute: Array<{
        messageHash: string;
        alert: SecurityAlert;
        count: number;
        timeRemaining: number; // Kalan süre
    }> = [];

    // Kuyruktaki mesaj sayısını takip et
    private queuedMessageCount$ = new BehaviorSubject<number>(0);

    // Sessize alma durumunu getter ve setter ile kontrol et
    get isMuted(): boolean {
        return this._isMuted;
    }

    set isMuted(value: boolean) {
        this._isMuted = value;
        this.toggleMuteCountdown(value);
    }

    constructor(public toastr: ToastrService,
        private translateService: TranslateService,
        private maintenanceHub: MaintenanceHubConnectionService,
        private postOfficeService: PostOfficeService,
        private settingsService: SettingsService,  private router: Router) { }

    public setToastContainer(container: any) {
        // ToastrService içinde özel konteyneri tanımlayın
        this.toastr.overlayContainer = container;
    }

    /**
     * Genel Alert gösterme fonksiyonu - SecurityAlertComponent kullanır
     * Tüm alert tipleri için: success, error, info, warning
     * Duplicate mesajları sayaçla gösterir
     */
    showGeneralAlert(options: GeneralAlertOptions): any {
        // Eğer mute edilmişse, kuyruğa normal şekilde ekle (duplicate kontrolü yok)
        if (this.isMuted) {
            this.generalAlertQueue.push(options);
            this.queuedMessageCount$.next(this.queuedMessageCount); // Sayacı güncelle
            console.log('🔇 General alert queued, will show after unmute:', options.title);
            return null;
        }

        // Duplicate mesaj kontrolü için hash oluştur
        const messageHash = this.createMessageHash(options.title, options.content, options.alertType);

        // Eğer aynı mesaj zaten varsa, mevcut toast'ı kapat ve yeni count ile tekrar aç
        if (this.activeToastMap.has(messageHash)) {
            const existingToastData = this.activeToastMap.get(messageHash);
            const newCount = existingToastData.count + 1;

            // Eski toast'ı kapat
            this.toastr.remove(existingToastData.toastRef.toastId);
            this.activeToastMap.delete(messageHash);

            // Yeni toast'ı arttırılmış count ile oluştur
            return this.createNewToast(options, messageHash, newCount);
        }

        // İlk kez gösterilen mesaj
        return this.createNewToast(options, messageHash, 1);
    }

    /**
     * Yeni toast oluşturur
     */
    private createNewToast(options: GeneralAlertOptions, messageHash: string, count: number): any {
        let toastrOptions: GlobalConfig;
        toastrOptions = this.toastr.toastrConfig;
        const opt = cloneDeep(toastrOptions);

        // SecurityAlertComponent kullan
        opt.toastComponent = SecurityAlertComponent;
        opt.toastClass = 'toast';
        opt.enableHtml = false;
        opt.disableTimeOut = options.disableTimeOut ?? false;
        opt.timeOut = options.timeOut ?? (options.alertType === AlertType.Error ? TOAST_TIMEOUTS.DEFAULT_ERROR : TOAST_TIMEOUTS.DEFAULT_NORMAL);
        opt.tapToDismiss = options.tapToDismiss ?? true;
        opt.countDuplicates = false;
        opt.preventDuplicates = false; // Bizim logic ile handle ediyoruz
        opt.closeButton = options.closeButton ?? true;
        opt.positionClass = 'inline';

        // Hover sırasında geri sayımın durması ve kaldığı yerden devam etmesi için
        // default extendedTimeOut değerini kullan

        // SecurityAlert objesi oluştur
        const securityAlert = new SecurityAlert();
        securityAlert.title = options.title;
        securityAlert.content = options.content;
        securityAlert.alertType = options.alertType;
        securityAlert.date = new Date();
        securityAlert.photoUrl = options.photoUrl;
        securityAlert.showImage = options.showImage ?? false;
        securityAlert.detailsUrl = options.detailsUrl;
        securityAlert.detailsUrlParams = options.detailsUrlParams;
        securityAlert.locateUrl = options.locateUrl;
        securityAlert.locateUrlParams = options.locateUrlParams;
        securityAlert.count = count; // Arttırılan count

        // Toast göster
        const toastRef = this.toastr.show(JSON.stringify(securityAlert), '', opt);

        // Toast'ı Map'e kaydet
        this.activeToastMap.set(messageHash, {
            toastRef,
            alert: securityAlert,
            count: count
        });

        // Toast dismiss edildiğinde Map'ten kaldır
        toastRef.onHidden.subscribe(() => {
            this.activeToastMap.delete(messageHash);
        });

        return toastRef;
    }

    /**
     * Hızlı Success toast
     */
    showSuccess(title: string, content: string, options?: Partial<GeneralAlertOptions>) {
        return this.showGeneralAlert({
            title,
            content,
            alertType: AlertType.Success,
            timeOut: TOAST_TIMEOUTS.SUCCESS,
            ...options
        });
    }

    /**
     * Hızlı Error toast
     */
    showError(title: string, content: string, options?: Partial<GeneralAlertOptions>) {
        return this.showGeneralAlert({
            title,
            content,
            alertType: AlertType.Error,
            timeOut: TOAST_TIMEOUTS.ERROR,
            disableTimeOut: false,
            ...options
        });
    }

    /**
     * Hızlı Info toast
     */
    showInfo(title: string, content: string, options?: Partial<GeneralAlertOptions>) {
        return this.showGeneralAlert({
            title,
            content,
            alertType: AlertType.Info,
            timeOut: TOAST_TIMEOUTS.INFO,
            ...options
        });
    }

    /**
     * Hızlı Warning toast
     */
    showWarning(title: string, content: string, options?: Partial<GeneralAlertOptions>) {
        return this.showGeneralAlert({
            title,
            content,
            alertType: AlertType.Warning,
            timeOut: TOAST_TIMEOUTS.WARNING,
            ...options
        });
    }

    async clearNotifications(delay: number = 300, smoothClear: boolean = true) {
        if (!smoothClear) {
            this.clearNotifications();
            return;
        }

        const activeToasts = this.toastr.toasts.slice(); // Create a copy of the active toasts array
        for (const toast of activeToasts) {
            this.toastr.remove(toast.toastId);
            await new Promise(resolve => setTimeout(resolve, delay));
        }

    }

    toggleMute() {
        this.isMuted = !this.isMuted;
    }

    public get activeNotifications(): number {
        return this.toastr.currentlyActive;
    }

    /**
     * Kuyruktaki mesaj sayısını döndürür
     */
    public get queuedMessageCount(): number {
        return this.alertQueue.length + this.generalAlertQueue.length;
    }

    /**
     * Kuyruktaki mesaj sayısını observable olarak döndürür
     */
    public getQueuedMessageCount(): Observable<number> {
        return this.queuedMessageCount$.asObservable();
    }


    showMaintenanceAlert(data: Maintenance.FragmentationProgress) {
        // Yeni genel alert sistemini kullan
        const toastRef = this.showGeneralAlert({
            title: 'Database Optimization in Progress',
            content: `
                <p class="text-gray-700">
                    You may experience <span class="underline decoration-2 decoration-sky-500 font-bold">temporary slowdowns</span> until the process is complete.
                </p>
            `,
            alertType: AlertType.Info,
            detailsUrl: '/maintenances/database',
            disableTimeOut: true,
            tapToDismiss: false
        });

        // Pass the currentProgress dynamically to the toast component
        if (toastRef.toastRef.componentInstance) {
            this.maintenanceHub.fragmentationProgress$.subscribe((md) => {
                toastRef.toastRef.componentInstance.progress = md.progress;
                if (md.progress == 100) {
                    const alertInstance = toastRef.toastRef.componentInstance.alert;

                    alertInstance.alertType = AlertType.Success;
                    alertInstance.title = 'Database Optimization Completed';
                    alertInstance.content = `
                        <div class="text-gray-700">
                            <span>Your database has been <span class="underline decoration-2 decoration-green-500 font-bold">successfully optimized</span>. Please note that the system may experience temporary slowdowns as the changes take effect. This is expected and should resolve shortly as the system stabilizes.</span>
                        </div>`;

                    //püf noktası
                    toastRef.toastRef.componentInstance.alert = alertInstance;
                    //dismiss dialog after 10 seconds
                    setTimeout(() => {
                        this.toastr.clear(toastRef.toastId);
                    }, TOAST_TIMEOUTS.MAINTENANCE);
                }
            });
        }
    }


    // Geri sayımı başlatır veya durdurur
    private toggleMuteCountdown(isMuted: boolean) {
        if (isMuted) {
            // Mevcut toast'ları gizle ve sakla (silme!)
            this.hideCurrentToasts();

            // Countdown başlat
            this.countdown$.next(this.muteDuration);
            this.countdownInterval = setInterval(() => {
                const current = this.countdown$.value;
                if (current > 0) {
                    this.countdown$.next(current - 1);
                } else {
                    // Süre doldu, otomatik unmute
                    this.clearMuteCountdown();
                    this.restoreHiddenToasts(); // Gizlenen toast'ları geri yükle
                    this.displayQueuedAlerts(); // Queue'daki yeni alertleri göster
                }
            }, 1000);
        } else {
            // Manuel unmute
            this.clearMuteCountdown();
            this.restoreHiddenToasts(); // Gizlenen toast'ları geri yükle
            this.displayQueuedAlerts(); // Queue'daki yeni alertleri göster
        }
    }

    // Geri sayım intervalini temizler
    private clearMuteCountdown() {
        clearInterval(this.countdownInterval);
        this._isMuted = false;
        this.countdown$.next(0);
        // Queue temizlendiğinde sayacı da güncelle
        this.queuedMessageCount$.next(this.queuedMessageCount);
    }

    // Kuyrukta biriken bildirimleri sırayla gösterir
    private displayQueuedAlerts() {
        setTimeout(async () => {
            // Önce TagAlert kuyruğunu işle
            while (this.alertQueue.length > 0) {
                const alert = this.alertQueue.shift();
                this.queuedMessageCount$.next(this.queuedMessageCount); // Sayacı güncelle
                if (alert) {
                    this.showAlert(alert);
                    await new Promise(resolve => setTimeout(resolve, 250));
                }
            }

            // Sonra GeneralAlert kuyruğunu işle
            while (this.generalAlertQueue.length > 0) {
                const generalAlert = this.generalAlertQueue.shift();
                this.queuedMessageCount$.next(this.queuedMessageCount); // Sayacı güncelle
                if (generalAlert) {
                    // Queue'dan çıkan mesajı normal pipeline'a gönder
                    // Bu sayede mevcut duplicate kontrolü çalışır
                    // Eğer timeout tanımsızsa varsayılan değerleri ayarla
                    if (generalAlert.disableTimeOut === undefined) {
                        generalAlert.disableTimeOut = false;
                    }

                    if (!generalAlert.timeOut || generalAlert.timeOut <= 0) {
                        generalAlert.timeOut = TOAST_TIMEOUTS.DEFAULT_NORMAL;
                    }

                    this.showGeneralAlert(generalAlert);
                    await new Promise(resolve => setTimeout(resolve, 250));
                }
            }
        }, 250);
    }

    // Sessize alma süresi boyunca bildirimleri kuyrukta biriktirir
    async showAlert(tagAlert: TagAlert) {
        if (this.isMuted) {
            this.alertQueue.push(tagAlert);
            this.queuedMessageCount$.next(this.queuedMessageCount); // Sayacı güncelle
            return;
        }

        // Normal gösterim kodları burada devam ediyor...
        var tag = await this.postOfficeService.getTag(tagAlert.tagId, undefined);

        if (tag.trackItemId == null) return;

        var trackItem = await this.postOfficeService.getTrackItem(tag.trackItemId);
        var infantTagTypeId = this.settingsService.getSettingValue('Infant.Baby.Tag.Type');
        let isInfantTag = tag.tagTypeId == infantTagTypeId;

        const title = tagAlert.alert == AlertValidationResult.Alert
            ? this.translateService.instant('SystemAdmin.Alerts.SecurityAlert.Title')
            : this.translateService.instant('SystemAdmin.Alerts.SecurityAlert.Resolved.Title');

        const alertType = tagAlert.alert == AlertValidationResult.Alert ? AlertType.Error : AlertType.Success;

        const message = this.translateService.instant(
            tagAlert.alert == AlertValidationResult.Alert
                ? 'SystemAdmin.Alerts.SecurityAlert.SensorDescription'
                : 'SystemAdmin.Alerts.SecurityAlert.Resolved.SensorDescription',
            {
                sensorProperty: tagAlert.propertyName,
                trackItemDescription: trackItem.description,
                tagName: tag.name
            }
        );

        const content = `<p>${message}</p>`;

        let detailsUrl, detailsUrlParams;
        if (isInfantTag) {
            detailsUrl = `/object-safeties`;
            detailsUrlParams = { 'child-tag': tagAlert.tagId };
        }

        // showGeneralAlert kullanarak duplicate detection'ı etkinleştir
        this.showGeneralAlert({
            title: title,
            content: content,
            alertType: alertType,
            photoUrl: trackItem.photoUrl,
            showImage: true,
            locateUrl: '/live',
            locateUrlParams: { locate: 'tag', id: tagAlert.tagSessionHistoryId },
            detailsUrl: detailsUrl,
            detailsUrlParams: detailsUrlParams,
            timeOut: TOAST_TIMEOUTS.SECURITY_ALERT,
            disableTimeOut: false,
            tapToDismiss: true,
            closeButton: true
        });
    }

    // Geri sayımı takip eden observable (komponentte göstermek için)
    getCountdown(): Observable<number> {
        return this.countdown$.asObservable();
    }

    /**
     * Mesaj için unique hash oluşturur
     */
    private createMessageHash(title: string, content: string, alertType: AlertType): string {
        const hashInput = `${title}|${content}|${alertType}`;
        // Basit hash function (production'da daha güçlü bir hash kullanılabilir)
        let hash = 0;
        for (let i = 0; i < hashInput.length; i++) {
            const char = hashInput.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // 32-bit integer
        }
        return hash.toString();
    }

    /**
     * Mevcut toast'ları gizler ve bilgilerini saklar
     */
    private hideCurrentToasts() {
        this.hiddenToastsBeforeMute = [];

        // Mevcut aktif toast'ları kaydet
        this.activeToastMap.forEach((toastData, messageHash) => {
            const { toastRef, alert, count } = toastData;

            // Kalan süreyi hesapla (eğer timeout varsa)
            let timeRemaining = 0;
            if (toastRef.toastConfig && toastRef.toastConfig.timeOut && !toastRef.toastConfig.disableTimeOut) {
                // Toast'ın ne kadar süredir açık olduğunu hesapla
                const elapsedTime = Date.now() - alert.date.getTime();
                timeRemaining = Math.max(0, toastRef.toastConfig.timeOut - elapsedTime);
            }

            // Toast bilgilerini sakla
            this.hiddenToastsBeforeMute.push({
                messageHash,
                alert: { ...alert }, // Deep copy
                count,
                timeRemaining
            });

            // Toast'ı kapat (ama Map'ten hemen silme, onHidden'da silinecek)
            this.toastr.remove(toastRef.toastId);
        });

        console.log(`🔇 ${this.hiddenToastsBeforeMute.length} toast gizlendi (mute)`);
    }

    /**
     * Mute öncesi gizlenen toast'ları tekrar gösterir
     */
    private restoreHiddenToasts() {
        if (this.hiddenToastsBeforeMute.length === 0) {
            return;
        }

        console.log(`🔊 ${this.hiddenToastsBeforeMute.length} toast geri yükleniyor (unmute)`);

        // Gizlenen toast'ları sırayla tekrar göster
        setTimeout(async () => {
            for (const hiddenToast of this.hiddenToastsBeforeMute) {
                const { alert, count, timeRemaining } = hiddenToast;

                // Toast options'ını yeniden oluştur
                const options: GeneralAlertOptions = {
                    title: alert.title,
                    content: alert.content,
                    alertType: alert.alertType,
                    photoUrl: alert.photoUrl,
                    showImage: alert.showImage,
                    detailsUrl: alert.detailsUrl,
                    detailsUrlParams: alert.detailsUrlParams,
                    locateUrl: alert.locateUrl,
                    locateUrlParams: alert.locateUrlParams,
                    timeOut: timeRemaining > 1000 ? timeRemaining : TOAST_TIMEOUTS.DEFAULT_NORMAL, // En az 1 saniye
                    disableTimeOut: timeRemaining <= 0, // Eğer süre bittiyse timeout'u devre dışı bırak
                    tapToDismiss: true,
                    closeButton: true
                };

                // Hash'i yeniden oluştur
                const messageHash = this.createMessageHash(alert.title, alert.content, alert.alertType);

                // Toast'ı tekrar göster
                this.createNewToast(options, messageHash, count);

                // Toastlar arasında kısa bekleme
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Hidden list'i temizle
            this.hiddenToastsBeforeMute = [];
        }, 200);
    }

    /**
     * Test toast'ı oluştur - mute özelliğini test etmek için
     */
    createTestToast() {
        this.showInfo('Test Mute Toast', 'Bu mesaj mute butonunu test etmek için. Mute butonuna basınca gizlenmeli ve unmute ile geri gelmeli.', {
            timeOut: TOAST_TIMEOUTS.TEST_LONG,
            disableTimeOut: false
        });
        console.log('🧪 Test toast oluşturuldu - mute testi için');
    }

    /**
     * Multiple test toast oluştur - counter test etmek için
     */
    createMultipleTestToasts() {
        for (let i = 1; i <= 3; i++) {
            setTimeout(() => {
                this.showWarning(`Test Toast ${i}`, `Bu ${i}. test mesajı. Mute durumunda sayaç artar.`, {
                    timeOut: TOAST_TIMEOUTS.TEST_LONG,
                    disableTimeOut: false
                });
            }, i * 500); // Her 500ms'de bir toast
        }
        console.log('🧪 3 test toast oluşturulacak - counter testi için');
    }

    showRedirectAlert(url: string, message: string, countdownSeconds: number = 10): void {
        let options: GlobalConfig;
        options = this.toastr.toastrConfig;
        const opt = cloneDeep(options);
        opt.toastComponent = SecurityAlertComponent;
        opt.toastClass = 'toast';
        opt.enableHtml = false;
        opt.disableTimeOut = true;
        opt.tapToDismiss = false;
        opt.countDuplicates = false;
        opt.preventDuplicates = true;
        opt.closeButton = true;
        opt.positionClass = 'inline';

        const sa = new SecurityAlert();
        sa.date = new Date();
        sa.alertType = AlertType.Info;
        sa.title = 'Yönlendirme Bildirimi';
        sa.content = `
        <div class="text-gray-700">
            <p>${message}</p>
            <p class="mt-2 text-sm text-blue-600">
                <span class="font-semibold">${countdownSeconds}</span> saniye içinde yönlendirileceksiniz...
            </p>
        </div>`;

        const toastRef = this.toastr.show(JSON.stringify(sa), '', opt);

        let remainingSeconds = countdownSeconds;
        const countdownInterval = setInterval(() => {
            remainingSeconds--;
            
            if (remainingSeconds <= 0) {
                clearInterval(countdownInterval);
                this.toastr.clear(toastRef.toastId);
                
                // URL'nin internal mi external mi olduğunu kontrol et
                const isInternalUrl = url.startsWith('/') && !url.startsWith('//');
                
                if (isInternalUrl) {
                    // Internal URL için Angular router kullan
                    this.router.navigateByUrl(url);
                } else {
                    // External URL için window.location kullan
                    window.location.href = url;
                }
                return;
            }

            const alertInstance = toastRef.toastRef.componentInstance.alert;
            alertInstance.content = `
            <div class="text-gray-700">
                <p>${message}</p>
                <p class="mt-2 text-sm text-blue-600">
                    <span class="font-semibold">${remainingSeconds}</span> saniye içinde yönlendirileceksiniz...
                </p>
            </div>`;
            toastRef.toastRef.componentInstance.alert = alertInstance;
            toastRef.toastRef.componentInstance.progress = ((countdownSeconds - remainingSeconds) / countdownSeconds) * 100;
        }, 1000);

        // Kullanıcı bildirimi kapatırsa interval'i temizle
        toastRef.toastRef.componentInstance.manualClose.subscribe(() => {
            clearInterval(countdownInterval);
        });
    }

}

